/**
 * spotify-bridge.js
 *
 * Spotify integration — mirrors the MusicKitBridge interface so
 * WebampController can drive either service without modification.
 *
 * Auth:     PKCE OAuth 2.0 (no server required)
 * Playback: Spotify Web Playback SDK (requires Premium)
 * Data:     Spotify Web API
 *
 * Usage:
 *   const sp = new SpotifyBridge(SPOTIFY_CLIENT_ID);
 *   // On page load, check for auth callback:
 *   if (sp.handleCallbackIfPresent()) await sp.restoreSession();
 *   // Connect:
 *   await sp.authorize();       // redirects to Spotify
 *   // After redirect back, init the player:
 *   await sp.initPlayer();
 */

const SPOTIFY_AUTH_URL = 'https://accounts.spotify.com/authorize';
const SPOTIFY_TOKEN_URL = 'https://accounts.spotify.com/api/token';
const SPOTIFY_API_BASE = 'https://api.spotify.com/v1';

const SCOPES = [
  'streaming',
  'user-read-email',
  'user-read-private',
  'user-library-read',
  'playlist-read-private',
  'playlist-read-collaborative',
  'user-read-playback-state',
  'user-modify-playback-state',
].join(' ');

// Normalised playback states — same shape used by WebampController
export const SpotifyPlaybackStates = {
  none:      'none',
  loading:   'loading',
  playing:   'playing',
  paused:    'paused',
  stopped:   'stopped',
  ended:     'ended',
  completed: 'completed',
  waiting:   'waiting',
};

// ── PKCE helpers ──────────────────────────────────────────────────────────────

function generateCodeVerifier(length = 128) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  const arr = new Uint8Array(length);
  crypto.getRandomValues(arr);
  return Array.from(arr, (b) => chars[b % chars.length]).join('');
}

async function generateCodeChallenge(verifier) {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// ─────────────────────────────────────────────────────────────────────────────

export class SpotifyBridge extends EventTarget {
  /** @param {string} clientId  Your Spotify application Client ID */
  constructor(clientId) {
    super();
    this.clientId = clientId;

    this._accessToken  = null;
    this._refreshToken = null;
    this._tokenExpires = 0;
    this._deviceId     = null;
    this._player       = null;
    this._currentState = null;   // Last Spotify.PlaybackState
    this._currentIndex = 0;      // Tracked manually (Spotify doesn't expose index)
    this._prevTrackId  = null;
    this._navigating   = false;  // True while we're initiating a navigation
    this._timeInterval = null;
    this._lastStateTs  = 0;      // Date.now() when _currentState was captured

    // Mirror MusicKit's PlaybackStates shape
    this.PlaybackStates = SpotifyPlaybackStates;
  }

  // ─────────────────────────────────────────────────────────────
  // Auth — PKCE OAuth 2.0
  // ─────────────────────────────────────────────────────────────

  get isAuthorized() {
    return !!this._accessToken && Date.now() < this._tokenExpires;
  }

  /** Clear stored tokens and disconnect the playback SDK player. */
  disconnect() {
    localStorage.removeItem('sp_access_token');
    localStorage.removeItem('sp_refresh_token');
    localStorage.removeItem('sp_token_expires');
    this._accessToken  = null;
    this._refreshToken = null;
    this._tokenExpires = 0;
    if (this._player) {
      this._player.disconnect();
      this._player = null;
    }
    this._deviceId = null;
    this.dispatchEvent(new CustomEvent('disconnected'));
  }

  /**
   * Call on every page load. If a Spotify ?code= callback is present,
   * exchange it for tokens and clean the URL. Returns true if handled.
   */
  async handleCallbackIfPresent() {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    if (!code || !localStorage.getItem('sp_code_verifier')) return false;

    try {
      await this._exchangeCode(code);
    } catch (err) {
      console.error('[SpotifyBridge] Token exchange failed:', err);
    }

    // Remove ?code= from URL without a page reload
    const clean = location.pathname + location.hash;
    history.replaceState({}, '', clean);
    return true;
  }

  /** Restore tokens from localStorage. Returns true if a valid session exists. */
  restoreSession() {
    const token   = localStorage.getItem('sp_access_token');
    const refresh = localStorage.getItem('sp_refresh_token');
    const exp     = parseInt(localStorage.getItem('sp_token_expires') ?? '0', 10);

    if (token && exp > Date.now()) {
      this._accessToken  = token;
      this._refreshToken = refresh;
      this._tokenExpires = exp;
      return true;
    }
    if (refresh) {
      this._refreshToken = refresh;
      return true; // can still refresh
    }
    return false;
  }

  /** Redirect the browser to Spotify's auth page (PKCE flow). */
  async authorize() {
    const verifier  = generateCodeVerifier();
    const challenge = await generateCodeChallenge(verifier);
    localStorage.setItem('sp_code_verifier', verifier);

    const params = new URLSearchParams({
      response_type:         'code',
      client_id:             this.clientId,
      scope:                 SCOPES,
      redirect_uri:          this._redirectUri(),
      code_challenge_method: 'S256',
      code_challenge:        challenge,
    });

    window.location.href = `${SPOTIFY_AUTH_URL}?${params}`;
  }

  /** Sign out and clear stored tokens. */
  signOut() {
    this._accessToken  = null;
    this._refreshToken = null;
    this._tokenExpires = 0;
    this._player?.disconnect();
    this._player   = null;
    this._deviceId = null;
    this._stopTimePolling();
    localStorage.removeItem('sp_access_token');
    localStorage.removeItem('sp_refresh_token');
    localStorage.removeItem('sp_token_expires');
  }

  async _exchangeCode(code) {
    const body = new URLSearchParams({
      grant_type:    'authorization_code',
      code,
      redirect_uri:  this._redirectUri(),
      client_id:     this.clientId,
      code_verifier: localStorage.getItem('sp_code_verifier'),
    });
    const res = await fetch(SPOTIFY_TOKEN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    });
    if (!res.ok) throw new Error(`Token exchange ${res.status}`);
    this._storeTokens(await res.json());
    localStorage.removeItem('sp_code_verifier');
  }

  async _refreshAccessToken() {
    if (!this._refreshToken) return false;
    try {
      const body = new URLSearchParams({
        grant_type:    'refresh_token',
        refresh_token: this._refreshToken,
        client_id:     this.clientId,
      });
      const res = await fetch(SPOTIFY_TOKEN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      if (!res.ok) return false;
      this._storeTokens(await res.json());
      return true;
    } catch { return false; }
  }

  _storeTokens({ access_token, refresh_token, expires_in }) {
    this._accessToken  = access_token;
    if (refresh_token) this._refreshToken = refresh_token;
    this._tokenExpires = Date.now() + (expires_in - 60) * 1000;
    localStorage.setItem('sp_access_token',  this._accessToken);
    if (this._refreshToken) localStorage.setItem('sp_refresh_token', this._refreshToken);
    localStorage.setItem('sp_token_expires', String(this._tokenExpires));
  }

  _redirectUri() {
    return `${location.origin}${location.pathname}`;
  }

  // ─────────────────────────────────────────────────────────────
  // Web Playback SDK
  // ─────────────────────────────────────────────────────────────

  /**
   * Load the Spotify Web Playback SDK and connect the in-browser player.
   * Resolves when the player is ready (device_id available).
   * Rejects if the account doesn't have Premium.
   */
  initPlayer() {
    return new Promise(async (resolve, reject) => {
      await this._ensureToken();

      const init = () => {
        const player = new Spotify.Player({
          name: 'Webamp × Spotify',
          getOAuthToken: async (cb) => {
            await this._ensureToken();
            cb(this._accessToken);
          },
          volume: 0.8,
        });

        this._player = player;

        player.addListener('ready', ({ device_id }) => {
          this._deviceId = device_id;
          console.log('[SpotifyBridge] Player ready, device_id:', device_id);
          // Transfer active playback to this SDK device so all REST API
          // commands (pause, seek, next, prev) work without "No active device"
          this._transferPlayback(device_id).catch(err =>
            console.warn('[SpotifyBridge] transferPlayback failed:', err)
          );
          resolve();
        });

        player.addListener('not_ready', ({ device_id }) => {
          console.warn('[SpotifyBridge] Device went offline:', device_id);
        });

        player.addListener('player_state_changed', (state) => {
          this._onStateChanged(state);
        });

        player.addListener('initialization_error', ({ message }) => {
          reject(new Error(`Spotify init: ${message}`));
        });

        player.addListener('authentication_error', ({ message }) => {
          console.error('[SpotifyBridge] Auth error:', message);
        });

        player.addListener('account_error', ({ message }) => {
          reject(new Error('Spotify Premium is required for Web Playback.'));
        });

        player.connect().then(ok => {
          if (!ok) reject(new Error('Spotify player failed to connect.'));
        });
      };

      // IMPORTANT: window.onSpotifyWebPlaybackSDKReady must be set BEFORE the
      // SDK script is injected — the SDK calls it synchronously on load.
      if (window.Spotify) {
        // SDK already loaded from a previous call — invoke init directly
        init();
      } else {
        window.onSpotifyWebPlaybackSDKReady = init;
        this._loadSDK();
      }
    });
  }

  _loadSDK() {
    if (window.Spotify) return Promise.resolve();
    return new Promise((resolve) => {
      const s = document.createElement('script');
      s.src = 'https://sdk.scdn.co/spotify-player.js';
      s.onload = resolve;
      document.head.appendChild(s);
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Bridge interface — mirrors MusicKitBridge
  // ─────────────────────────────────────────────────────────────

  get isPlaying() {
    return this._currentState ? !this._currentState.paused : false;
  }

  get currentTime() {
    if (!this._currentState) return 0;
    // Estimate position based on last captured state + elapsed time
    const elapsed = this.isPlaying ? (Date.now() - this._lastStateTs) : 0;
    return (this._currentState.position + elapsed) / 1000;
  }

  get duration() {
    const ms = this._currentState?.track_window?.current_track?.duration_ms;
    return ms ? ms / 1000 : 0;
  }

  get playbackState() {
    if (!this._currentState) return SpotifyPlaybackStates.stopped;
    return this._currentState.paused
      ? SpotifyPlaybackStates.paused
      : SpotifyPlaybackStates.playing;
  }

  get nowPlayingItemIndex() { return this._currentIndex; }

  async seekTo(seconds) {
    const dq = this._deviceId ? `&device_id=${this._deviceId}` : '';
    await this._apiCall(
      `/me/player/seek?position_ms=${Math.round(seconds * 1000)}${dq}`,
      { method: 'PUT' }
    );
  }

  async skipToNextItem() {
    this._navigating = true;
    this._currentIndex++;
    const dq = this._deviceId ? `?device_id=${this._deviceId}` : '';
    await this._apiCall(`/me/player/next${dq}`, { method: 'POST' });
    setTimeout(() => { this._navigating = false; }, 1000);
  }

  async skipToPreviousItem() {
    this._navigating = true;
    this._currentIndex = Math.max(0, this._currentIndex - 1);
    const dq = this._deviceId ? `?device_id=${this._deviceId}` : '';
    await this._apiCall(`/me/player/previous${dq}`, { method: 'POST' });
    setTimeout(() => { this._navigating = false; }, 1000);
  }

  async stop() {
    const dq = this._deviceId ? `?device_id=${this._deviceId}` : '';
    await this._apiCall(`/me/player/pause${dq}`, { method: 'PUT' }).catch(() => {});
  }

  async play() {
    const dq = this._deviceId ? `?device_id=${this._deviceId}` : '';
    await this._apiCall(`/me/player/play${dq}`, { method: 'PUT' }).catch(() => {});
  }

  async pause() {
    const dq = this._deviceId ? `?device_id=${this._deviceId}` : '';
    await this._apiCall(`/me/player/pause${dq}`, { method: 'PUT' }).catch(() => {});
  }

  /**
   * Load a playlist and start at a specific index.
   * @param {string} playlistId  Spotify playlist ID
   * @param {number} startIndex
   */
  async setQueue(playlistId, startIndex = 0) {
    this._navigating = true;
    this._currentIndex = startIndex;
    await this._apiCall(`/me/player/play?device_id=${this._deviceId}`, {
      method: 'PUT',
      body: JSON.stringify({
        context_uri: `spotify:playlist:${playlistId}`,
        offset:      { position: startIndex },
        position_ms: 0,
      }),
    });
    setTimeout(() => { this._navigating = false; }, 1500);
  }

  /** Play a single track by Spotify ID. */
  async playSong(trackId) {
    this._navigating = true;
    this._currentIndex = 0;
    await this._apiCall(`/me/player/play?device_id=${this._deviceId}`, {
      method: 'PUT',
      body: JSON.stringify({ uris: [`spotify:track:${trackId}`] }),
    });
    setTimeout(() => { this._navigating = false; }, 1000);
  }

  setVolume(fraction) {
    this._player?.setVolume(Math.max(0, Math.min(1, fraction))).catch(() => {});
  }

  // ─────────────────────────────────────────────────────────────
  // Library / Catalog API
  // ─────────────────────────────────────────────────────────────

  async getLibraryPlaylists() {
    const playlists = [];
    let path = '/me/playlists?limit=50';
    while (path) {
      const data = await this._apiCall(path);
      playlists.push(...(data?.items ?? []));
      path = data?.next ? data.next.replace(SPOTIFY_API_BASE, '') : null;
    }
    return playlists;
  }

  // Spotify recently-played returns tracks, not playlists — return empty
  // so the "Recently Played" section is simply omitted for Spotify.
  async getRecentPlaylists() { return []; }

  async getPlaylistTracks(playlistId) {
    // February 2026: /playlists/{id}/tracks removed — use /playlists/{id}/items
    // Response field also renamed: item.track → item.item
    const tracks = [];
    let path = `/playlists/${playlistId}/items?limit=50`;
    while (path) {
      const data = await this._apiCall(path);
      const items = (data?.items ?? []).filter(i => i?.item?.id ?? i?.track?.id);
      // Support both new `item` field and old `track` field for resilience
      tracks.push(...items.map(i => i.item ?? i.track));
      path = data?.next ? data.next.replace(SPOTIFY_API_BASE, '') : null;
    }
    return tracks;
  }

  async searchCatalog(query) {
    if (!query.trim()) return { songs: [], albums: [], artists: [] };
    // February 2026: search limit max reduced to 10 (was 50), default is now 5
    const data = await this._apiCall(
      `/search?q=${encodeURIComponent(query.trim())}&type=track&limit=10`
    );
    return {
      songs:   data?.tracks?.items ?? [],
      albums:  [],
      artists: [],
    };
  }

  /**
   * Convert a Spotify track into the shape Webamp expects.
   * Mirrors MusicKitBridge.toWebampTrack().
   */
  static toWebampTrack(track) {
    const artUrl = track.album?.images?.[0]?.url ?? null;
    return {
      url: '/silent.mp3',
      metaData: {
        title:  track.name ?? 'Unknown Title',
        artist: track.artists?.[0]?.name ?? 'Unknown Artist',
        album:  track.album?.name ?? '',
        ...(artUrl ? { albumArtUrl: artUrl } : {}),
      },
      duration: track.duration_ms ? Math.round(track.duration_ms / 1000) : undefined,
      _spotifyId: track.id,
    };
  }

  // ─────────────────────────────────────────────────────────────
  // Internal — state handling
  // ─────────────────────────────────────────────────────────────

  _onStateChanged(state) {
    if (!state) return;

    const wasPlaying = this._currentState ? !this._currentState.paused : false;
    this._currentState = state;
    this._lastStateTs  = Date.now();

    const track    = state.track_window?.current_track;
    const trackId  = track?.id;
    const isPlaying = !state.paused;

    // Start/stop the timeupdate polling interval
    if (isPlaying) {
      this._startTimePolling();
    } else {
      this._stopTimePolling();
    }

    // Dispatch playstate event
    this.dispatchEvent(new CustomEvent('playstate', {
      detail: {
        isPlaying,
        state: isPlaying ? SpotifyPlaybackStates.playing : SpotifyPlaybackStates.paused,
      },
    }));

    // Dispatch trackchange when the track actually changes
    if (trackId && trackId !== this._prevTrackId) {
      this._prevTrackId = trackId;

      // Auto-advance (Spotify moved to next track on its own)
      if (!this._navigating && wasPlaying && isPlaying && this._prevTrackId) {
        this._currentIndex++;
      }

      this.dispatchEvent(new CustomEvent('trackchange', {
        detail: this._normalizeTrack(track),
      }));
    }

    // Queue completed: paused with no next tracks and wasn't already paused
    if (
      state.paused &&
      !state.track_window?.next_tracks?.length &&
      wasPlaying
    ) {
      this.dispatchEvent(new CustomEvent('playstate', {
        detail: { isPlaying: false, state: SpotifyPlaybackStates.completed },
      }));
    }
  }

  /** Emit timeupdate events while playing so Webamp's scrubber stays current. */
  _startTimePolling() {
    if (this._timeInterval) return;
    this._timeInterval = setInterval(() => {
      if (!this._currentState || this._currentState.paused) return;
      this.dispatchEvent(new CustomEvent('timeupdate', {
        detail: { currentTime: this.currentTime, duration: this.duration },
      }));
    }, 500);
  }

  _stopTimePolling() {
    if (this._timeInterval) {
      clearInterval(this._timeInterval);
      this._timeInterval = null;
    }
  }

  /** Normalize a Spotify track into the Apple Music resource shape panels expect. */
  _normalizeTrack(track) {
    return {
      id:   track.id,
      type: 'spotify-track',
      attributes: {
        name:             track.name,
        artistName:       track.artists?.[0]?.name ?? '',
        albumName:        track.album?.name ?? '',
        artwork:          track.album?.images?.[0] ? { url: track.album.images[0].url } : null,
        durationInMillis: track.duration_ms,
      },
    };
  }

  // ─────────────────────────────────────────────────────────────
  // Internal — API helper
  // ─────────────────────────────────────────────────────────────

  /**
   * Tell Spotify's backend that this SDK device is the active player.
   * Must be called after the 'ready' event; without this, REST API commands
   * like /me/player/pause return 404 "No active device found".
   */
  async _transferPlayback(deviceId) {
    await this._apiCall('/me/player', {
      method: 'PUT',
      body: JSON.stringify({ device_ids: [deviceId], play: false }),
    });
    console.log('[SpotifyBridge] Playback transferred to SDK device.');
  }

  async _ensureToken() {
    if (this._accessToken && Date.now() < this._tokenExpires) return;
    const ok = await this._refreshAccessToken();
    if (!ok) throw new Error('Spotify session expired — please reconnect.');
  }

  async _apiCall(path, options = {}) {
    await this._ensureToken();
    const url = path.startsWith('http') ? path : `${SPOTIFY_API_BASE}${path}`;
    const res = await fetch(url, {
      ...options,
      headers: {
        Authorization:  `Bearer ${this._accessToken}`,
        'Content-Type': 'application/json',
        ...(options.headers ?? {}),
      },
    });
    // 204 No Content and 202 Accepted are successful non-body responses
    if (res.status === 204 || res.status === 202) return null;
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const message = err?.error?.message ?? res.statusText;
      console.warn(`[SpotifyBridge] API ${res.status} on ${url}:`, message, err);

      // 401 = token expired or revoked → clear session and force re-auth
      if (res.status === 401) {
        console.warn('[SpotifyBridge] 401 — token expired or revoked. Clearing session.');
        localStorage.removeItem('sp_access_token');
        localStorage.removeItem('sp_refresh_token');
        localStorage.removeItem('sp_token_expires');
        this._accessToken  = null;
        this._refreshToken = null;
        this._tokenExpires = 0;
        this.dispatchEvent(new CustomEvent('disconnected'));
      }
      // 403 = forbidden for this specific resource (private playlist, wrong owner, etc.)
      // Do NOT clear the session — the token is still valid for other calls.
      throw new Error(`Spotify ${res.status}: ${message}`);
    }
    return res.json();
  }
}
